import BaseView, { DomainModel3D } from './base-view';
import DoorView from './door-view';
import GenericAssetView from './generic-asset-view';
import { Group } from 'three';
import { Murphybed } from '../domain';
import { clamp } from '../math/clamp';

const PIVOT_HEIGHT = 13;

export default class MurphybedView extends BaseView {

    declare model: Murphybed;
   
    pivot: Group;
    movables: Group;
    mattressOrientation: Group;

    mattress: GenericAssetView;
    door: DoorView;

    constructor(model: DomainModel3D) {
        super(model);
        this.init();

        this.onReady.name = 'MurphybedView';
    }

    make() {
        this.name = 'Murphybed';
        this.mattress = new GenericAssetView(this.model.mattress);
        this.mattress.name = 'Mattress';
        this.mattress.userData.suppressExport = false;

        // note that the door gets attached later when we bind changeDoorType
        this.door = new DoorView(this.model.door);

        this.pivot = new Group();
        this.movables = new Group();
        this.mattressOrientation = new Group(); 

        this.mattressOrientation.add(this.mattress);
        this.movables.add(this.mattressOrientation);
        this.pivot.add(this.movables);
        this.add(this.pivot);

        this.pivot.position.set(0, PIVOT_HEIGHT - this.model.position.y, this.model.depth);
        this.movables.position.y = -this.pivot.position.y;
    }

    bind() {
        this.model.bind('width', this.changeSize);
        this.model.on('height', this.changeSize);
        this.model.on('size', this.changeSize);

        this.model.door.bind('hinge', this.changeDoorType);

        this.model.bind('open', () => {
            if(this.model.door.hinge.startsWith('bi-fold')) {
                const
                    open = this.model.open,
                    open2 = 2*open,
                    t = clamp(open2, 1, 2) - 1;
                this.model.door.open = clamp(open2, 0, 0.9);
                this.pivot.rotation.set(t*Math.PI/2, 0, 0);
            } else {
                this.pivot.rotation.set(this.model.open*Math.PI/2, 0, 0);
            }
        });

        this.model.bind('opacity', () => {
            this.mattress.model.opacity = this.model.opacity;
            this.door.model.opacity = this.model.opacity;
        });
    }

    /**
     * Changes the attachment of the door view to either the main view or the
     * movables group under the pivot
     */
    changeDoorType = () => {
        if(this.model.door.hinge.startsWith('bi-fold')) {
            this.door.removeFromParent();
            this.add(this.door);
        } else {
            this.door.removeFromParent();
            this.movables.add(this.door);
        }
    }

    changeSize = () => {
        let width: number, height: number;
        switch(this.model.size) {
            case 'queen':
                width = 60;
                height = 75;
                break;
            case 'full':
                width = 54;
                height = 74;
                break;
            case 'twin':
                width = 38;
                height = 74;
                break;
        }
        if(!this.model.width) this.model.width = width;
        if(!this.model.height) this.model.height = height;
        if(this.model.direction == 'horizontal') {
            let w = height, h = width;
            this.mattressOrientation.rotation.set(-Math.PI/2, Math.PI/2, 0);
            this.mattress.position.set(
                -this.model.height + (this.model.height - h) / 2,
                0,
                (this.model.width - w) / 2,
                );
        } else {
            this.mattressOrientation.rotation.set(-Math.PI/2, 0, 0);
            this.mattress.position.set(
                (this.model.width - width) / 2,
                0,
                (this.model.height - height)/2);
        }
            
        this.model.door.batch(() => {
            this.model.door.width = this.model.width;
            this.model.door.height = this.model.height;
        });
    }
}
